import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import AlvnaBrand from "../components/AlvnaBrand";
import ReceiptView from "../components/ReceiptView";
import useRestaurantName from "../useRestaurantName";
import useClockState from "../useClockState";
import TabbedStation from "../components/TabbedStation";
import ClockBanner from "../components/ClockBanner";
import HelpButton from "../components/HelpButton";
import { endpoints } from "../apiConfig";
import { getAuthHeader, getOperatorIdentity, getOperatorLoginRedirect, clearOperatorSession } from "../session";
import { colors, fonts, space, radius, buttonPrimary, buttonSecondary } from "../theme";

const authHeader = () => getAuthHeader();

function elapsedSince(dateStr) {
  if (!dateStr) return "";
  const mins = Math.floor((Date.now() - new Date(dateStr).getTime()) / 60000);
  if (mins < 1) return "Just seated";
  if (mins < 60) return `Since ${mins}m`;
  return `Since ${Math.floor(mins / 60)}h ${mins % 60}m`;
}

function WaiterBoard() {
  const [view, setView] = useState("tables"); // tables | order
  const [tables, setTables] = useState([]);
  const [menu, setMenu] = useState([]);
  const [activeTable, setActiveTable] = useState(null);
  const [currentOrder, setCurrentOrder] = useState(null);
  const [cart, setCart] = useState({}); // { key: { name, price, quantity, item_id?, options? } } — key = item id, or "id|choiceIds" for items with dropdown options
  const [optionItem, setOptionItem] = useState(null);
  const [optionPicks, setOptionPicks] = useState({});
  const [partySize, setPartySize] = useState("");
  const [kitchenNote, setKitchenNote] = useState("");
  const [tableNote, setTableNote] = useState("");
  const [editingNote, setEditingNote] = useState(false);
  const [noteDraft, setNoteDraft] = useState("");
  const [history, setHistory] = useState([]);
  const [settings, setSettings] = useState({ tax_rate: 0, gratuity_rate: 0, gratuity_min_party_size: 0, split_bill_enabled: false });
  const [receipt, setReceipt] = useState(null);
  const restaurantName = useRestaurantName();
  const { clock, blocked: readOnly } = useClockState();
  const [previewReceipt, setPreviewReceipt] = useState(null);
  const [staff, setStaff] = useState(null);
  const [error, setError] = useState("");
  const [detailsSaved, setDetailsSaved] = useState(false);
  const [detailsSaveError, setDetailsSaveError] = useState("");
  const [, forceTick] = useState(0);

  useEffect(() => {
    if (!getAuthHeader().Authorization) {
      window.location.href = getOperatorLoginRedirect("waiter");
      return;
    }
    setStaff(getOperatorIdentity());
    loadTables();
    axios.get(endpoints.settings, { headers: authHeader() }).then((res) => setSettings(res.data)).catch(() => {});
    const tick = setInterval(() => forceTick((n) => n + 1), 30000);
    return () => clearInterval(tick);
  }, []);

  const loadTables = () => {
    axios.get(endpoints.waiterTables, { headers: authHeader() })
      .then((res) => setTables(res.data))
      .catch((err) => {
        if (err.response?.status === 401) window.location.href = getOperatorLoginRedirect("waiter");
      });
  };

  const openTable = async (table) => {
    setActiveTable(table);
    setError("");
    setCart({});
    setPreviewReceipt(null);
    const res = await axios.get(`${endpoints.waiterOrder}?table_id=${table.id}`, { headers: authHeader() });
    setCurrentOrder(res.data.order);
    setPartySize(res.data.order?.party_size ?? "");
    setKitchenNote(res.data.order?.kitchen_note ?? "");
    setTableNote(table.note || "");
    const menuRes = await axios.get(endpoints.waiterMenu, { headers: authHeader() });
    setMenu(menuRes.data);
    const histRes = await axios.get(endpoints.orderHistory(table.id), { headers: authHeader() });
    setHistory(histRes.data);
    setView("order");
  };

  const incrementCartItem = (item) => {
    if (item.option_groups && item.option_groups.length) {
      const picks = {};
      item.option_groups.forEach((g) => {
        if (g.multi_select) { const d = g.choices.filter((c) => c.is_default && c.is_available).map((c) => c.id); if (d.length) picks[g.id] = d; }
        else if (g.required) { const d = g.choices.find((c) => c.is_default && c.is_available) || g.choices.find((c) => c.is_available); if (d) picks[g.id] = d.id; }
      });
      setOptionPicks(picks);
      setOptionItem(item);
      return;
    }
    setCart((prev) => {
      const existing = prev[item.id];
      return {
        ...prev,
        [item.id]: {
          name: item.name,
          price: Number(item.price),
          quantity: (existing?.quantity || 0) + 1,
        },
      };
    });
  };

  const optionResolve = (item, picks) => {
    let extra = 0; const ids = []; const parts = []; let missing = false;
    item.option_groups.forEach((g) => {
      if (g.multi_select) {
        const chosen = Array.isArray(picks[g.id]) ? picks[g.id] : [];
        const cs = g.choices.filter((c) => chosen.includes(c.id));
        if (!cs.length) { if (g.required) missing = true; return; }
        cs.forEach((c) => { ids.push(c.id); extra += Math.round(c.price_delta * 100); });
        parts.push(`${g.name}: ${cs.map((c) => c.label).join(", ")}`);
        return;
      }
      const c = g.choices.find((x) => x.id === picks[g.id]);
      if (!c) { if (g.required) missing = true; return; }
      ids.push(c.id); extra += Math.round(c.price_delta * 100); parts.push(`${g.name}: ${c.label}`);
    });
    return { ids, extra: extra / 100, text: parts.join("; "), missing };
  };
  const addWithOptions = (item, picks) => {
    const r = optionResolve(item, picks);
    const key = `${item.id}|${[...r.ids].sort((a, b) => a - b).join(",")}`;
    setCart((prev) => ({
      ...prev,
      [key]: { name: r.text ? `${item.name} (${r.text})` : item.name, price: Number(item.price) + r.extra, quantity: (prev[key]?.quantity || 0) + 1, item_id: item.id, options: r.ids },
    }));
    setOptionItem(null);
  };

  const adjustCartQty = (itemId, delta) => {
    setCart((prev) => {
      const next = { ...prev };
      const newQty = (next[itemId]?.quantity || 0) + delta;
      if (newQty <= 0) {
        delete next[itemId];
      } else {
        next[itemId] = { ...next[itemId], quantity: newQty };
      }
      return next;
    });
  };

  const cartEntries = Object.entries(cart);
  const cartCount = cartEntries.reduce((sum, [, c]) => sum + c.quantity, 0);
  const cartTotal = cartEntries.reduce((sum, [, c]) => sum + c.price * c.quantity, 0);

  const saveDetails = async () => {
    setDetailsSaved(false);
    setDetailsSaveError("");
    try {
      const res = await axios.post(
        endpoints.waiterOrder,
        { table_id: activeTable.id, items: [], party_size: partySize ? parseInt(partySize, 10) : null, kitchen_note: kitchenNote },
        { headers: authHeader() }
      );
      setCurrentOrder(res.data.order);
      setDetailsSaved(true);
      setTimeout(() => setDetailsSaved(false), 2000);
    } catch (err) {
      setDetailsSaveError(err.response?.data?.error || "Could not save.");
    }
  };

  const saveTableNote = async () => {
    await axios.patch(endpoints.waiterTableNote(activeTable.id), { note: noteDraft }, { headers: authHeader() });
    setTableNote(noteDraft);
    setEditingNote(false);
    loadTables();
  };

  const sendToKitchen = async () => {
    if (cartCount === 0) return;
    setError("");
    if (!currentOrder && !partySize) {
      setError("Party size is required before sending the first order to the kitchen.");
      return;
    }
    try {
      const res = await axios.post(
        endpoints.waiterOrder,
        {
          table_id: activeTable.id,
          items: cartEntries.map(([menuItemId, c]) => ({ menu_item_id: c.item_id || parseInt(menuItemId, 10), quantity: c.quantity, options: c.options || [] })),
          party_size: partySize ? parseInt(partySize, 10) : null,
          kitchen_note: kitchenNote,
        },
        { headers: authHeader() }
      );
      setCurrentOrder(res.data.order);
      setCart({});
      loadTables();
    } catch (err) {
      setError(err.response?.data?.error || "Could not send order.");
    }
  };

  const removeSentItem = async (itemId) => {
    await axios.delete(endpoints.waiterOrderDeleteItem(itemId), { headers: authHeader() });
    const res = await axios.get(`${endpoints.waiterOrder}?table_id=${activeTable.id}`, { headers: authHeader() });
    setCurrentOrder(res.data.order);
  };

  const markItemReceived = async (itemId) => {
    // Optimistic update so the tap feels instant.
    setCurrentOrder((prev) => ({
      ...prev,
      items: prev.items.map((i) => (i.id === itemId ? { ...i, status: "served" } : i)),
    }));
    try {
      await axios.patch(endpoints.orderItemStatus(itemId), { status: "served" }, { headers: authHeader() });
    } catch {
      const res = await axios.get(`${endpoints.waiterOrder}?table_id=${activeTable.id}`, { headers: authHeader() });
      setCurrentOrder(res.data.order);
    }
  };

  const viewReceiptPreview = () => {
    if (!currentOrder) return;
    const subtotal = currentOrder.subtotal;
    const minPartySize = settings.gratuity_min_party_size || 0;
    const gratuityApplies = minPartySize === 0 || (parseInt(partySize, 10) || 0) >= minPartySize;
    const effectiveGratuityRate = gratuityApplies ? settings.gratuity_rate : 0;
    const taxAmount = round2(subtotal * (settings.tax_rate / 100));
    const gratuityAmount = round2(subtotal * (effectiveGratuityRate / 100));
    setPreviewReceipt({
      order_id: currentOrder.id,
      table_label: activeTable.label,
      items: currentOrder.items.map((i) => ({ item_name: i.item_name, unit_price: i.unit_price, quantity: i.quantity })),
      subtotal,
      tax_amount: taxAmount,
      gratuity_amount: gratuityAmount,
      tip_amount: 0,
      total_amount: round2(subtotal + taxAmount + gratuityAmount),
      closed_at: null,
    });
  };

  const closeOrder = async () => {
    setError("");
    const tipInput = window.prompt("Tip amount collected (leave blank or 0 if none):", "0");
    if (tipInput === null) return;
    const tipAmount = parseFloat(tipInput) || 0;

    try {
      const res = await axios.post(endpoints.orderClose(currentOrder.id), { tip_amount: tipAmount }, { headers: authHeader() });
      setReceipt(res.data.receipt);
      setCurrentOrder(null);
      loadTables();
    } catch (err) {
      setError(err.response?.data?.error || "Could not close order.");
    }
  };

  const backToTables = () => {
    setActiveTable(null);
    setCurrentOrder(null);
    setCart({});
    setReceipt(null);
    setPreviewReceipt(null);
    setView("tables");
    loadTables();
  };

  const logout = () => {
    const redirect = getOperatorLoginRedirect("waiter");
    sessionStorage.removeItem("staff_token");
    sessionStorage.removeItem("staff");
    sessionStorage.removeItem("staff_tenant");
    localStorage.removeItem("token");
    localStorage.removeItem("tenant");
    window.location.href = redirect === "/login" ? "/login" : redirect;
  };

  if (receipt) {
    return (
      <div style={styles.page}>
        <ReceiptView receipt={receipt} restaurant={receipt.restaurant || settings.restaurant} />
        <button style={{ ...buttonPrimary, marginTop: space[4], width: "100%", maxWidth: 280 }} onClick={backToTables}>
          Back to Tables
        </button>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      {restaurantName && <div className="restaurant-title" style={styles.restaurantTitle} data-testid="restaurant-title">{restaurantName}</div>}
      <ClockBanner clock={clock} blocked={readOnly} />
      <div style={styles.header}>
        <div style={{ display: "flex", alignItems: "center", gap: space[3] }}>
          {view !== "tables" && (
            <button style={styles.backBtn} onClick={backToTables}>← Back to Tables</button>
          )}
          {view === "tables" && (
            <h1 style={styles.title}>Select a Table <HelpButton page="waiter" dark /></h1>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: space[2] }}>
          {view === "order" && currentOrder && (
            <>
              <button style={styles.ghostBtn} onClick={viewReceiptPreview}>View Receipt</button>
              {settings.split_bill_enabled && (
                <button style={styles.splitPayBtn} onClick={viewReceiptPreview}>Split & Pay</button>
              )}
            </>
          )}
          {staff && <span style={styles.staffName}>{staff.name}</span>}
          {(staff?.role === "manager" || staff?.role === "owner") && (
            <a href="/manager-home" style={styles.managerHomeLink}>Manager Home</a>
          )}
          <button style={styles.logoutBtn} onClick={logout}>Log Out</button>
        </div>
      </div>

      {error && <div style={styles.errorBanner}>{error}</div>}

      {previewReceipt && (
        <div style={styles.overlay} onClick={() => setPreviewReceipt(null)}>
          <div onClick={(e) => e.stopPropagation()}>
            <ReceiptView receipt={previewReceipt} restaurant={settings.restaurant} />
          </div>
        </div>
      )}

      {view === "tables" && (
        <div style={styles.tableGrid}>
          {tables.map((t) => (
            <button key={t.id} style={{ ...styles.tableCard, ...(t.is_occupied ? styles.tableOccupied : {}) }}
              onClick={() => openTable(t)}>
              <div style={styles.tableLabel}>{t.label}</div>
              <div style={styles.tableStatus}>
                {t.is_occupied ? elapsedSince(t.order_opened_at) : "Available"}
              </div>
            </button>
          ))}
          {tables.length === 0 && <p style={styles.emptyText}>No tables set up yet.</p>}
        </div>
      )}

      {view === "order" && (
        <div style={{ width: "100%", maxWidth: 900 }}>
          <h2 style={{ ...styles.title, marginBottom: space[3] }}>{activeTable?.label}</h2>

          <div style={styles.noteBar}>
            {editingNote ? (
              <>
                <input
                  style={styles.noteInput}
                  value={noteDraft}
                  onChange={(e) => setNoteDraft(e.target.value)}
                  placeholder="Add a note for this table"
                  autoFocus
                />
                <button style={styles.editNoteBtn} onClick={saveTableNote} disabled={readOnly}>Save</button>
              </>
            ) : (
              <>
                <span style={styles.noteText}>{tableNote || "No note for this table."}</span>
                <button style={styles.editNoteBtn} onClick={() => { setNoteDraft(tableNote); setEditingNote(true); }}>
                  Edit Note
                </button>
              </>
            )}
          </div>

          <div style={styles.detailsRow}>
            <div>
              <label style={styles.smallLabel}>Party Size *</label>
              <input style={styles.smallInput} type="number" min="1" value={partySize} onChange={(e) => setPartySize(e.target.value)} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <label style={styles.smallLabel}>Kitchen Note</label>
              <div style={styles.noteBar}>
                <input style={styles.noteInput} placeholder="e.g. rush, allergy: peanuts" value={kitchenNote} onChange={(e) => setKitchenNote(e.target.value)} />
                <button style={styles.editNoteBtn} onClick={saveDetails} disabled={readOnly}>Save</button>
                {detailsSaved && <span style={styles.savedBadge}>✓ Saved</span>}
              </div>
            </div>
          </div>
          {detailsSaveError && <div style={styles.errorBanner}>{detailsSaveError}</div>}

          <div style={styles.categoryPillsRow}>
            {menu.map((cat) => (
              <button key={cat.id} style={styles.categoryPill} onClick={() => document.getElementById(`cat-${cat.id}`)?.scrollIntoView({ behavior: "smooth", block: "start" })}>
                {cat.name}
              </button>
            ))}
          </div>

          {menu.map((cat) => (
            <div key={cat.id} id={`cat-${cat.id}`} style={styles.categorySection}>
              <h3 style={styles.categoryHeading}>{cat.name}</h3>
              <div style={styles.itemGrid}>
                {cat.items.map((item) => {
                  const inCartQty = Object.entries(cart).filter(([k]) => k === String(item.id) || k.startsWith(`${item.id}|`)).reduce((sum, [, c]) => sum + c.quantity, 0);
                  const inCart = inCartQty ? { quantity: inCartQty } : null;
                  return (
                    <button
                      key={item.id}
                      style={{ ...styles.itemCard, ...(inCart ? styles.itemCardSelected : {}) }}
                      onClick={() => incrementCartItem(item)} disabled={readOnly}
                    >
                      <div style={styles.itemName}>{item.name}</div>
                      <div style={styles.itemPrice}>${Number(item.price).toFixed(2)}</div>
                      {inCart && <span style={styles.itemBadge}>{inCart.quantity}</span>}
                    </button>
                  );
                })}
                {cat.items.length === 0 && <p style={styles.emptyText}>No items in this category yet.</p>}
              </div>
            </div>
          ))}

          {optionItem && (
            <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 1500, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }} onClick={() => setOptionItem(null)}>
              <div role="dialog" aria-label={`Options for ${optionItem.name}`} data-testid="waiter-options" style={{ background: "#fff", color: "#111827", borderRadius: 14, padding: 18, width: "100%", maxWidth: 380 }} onClick={(e) => e.stopPropagation()}>
                <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 10 }}>{optionItem.name}</div>
                {optionItem.option_groups.map((g) => (
                  <div key={g.id} style={{ marginBottom: 10, fontSize: 13 }}>
                    <div style={{ marginBottom: 4 }}>{g.name}{g.required ? "" : " (optional)"}{g.multi_select ? " — choose any number" : ""}</div>
                    {g.multi_select ? (
                      <div style={{ display: "grid", gap: 6 }}>
                        {g.choices.map((c) => {
                          const checked = Array.isArray(optionPicks[g.id]) && optionPicks[g.id].includes(c.id);
                          return (
                            <label key={c.id} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14, opacity: c.is_available ? 1 : 0.55 }}>
                              <input type="checkbox" checked={checked} disabled={!c.is_available} onChange={(e) => {
                                const cur = Array.isArray(optionPicks[g.id]) ? optionPicks[g.id] : [];
                                setOptionPicks({ ...optionPicks, [g.id]: e.target.checked ? [...cur, c.id] : cur.filter((x) => x !== c.id) });
                              }} />
                              {c.label}{c.price_delta > 0 ? ` (+$${c.price_delta.toFixed(2)})` : ""}{!c.is_available ? " (sold out)" : ""}
                            </label>
                          );
                        })}
                      </div>
                    ) : (
                      <select value={optionPicks[g.id] || ""} onChange={(e) => setOptionPicks({ ...optionPicks, [g.id]: e.target.value ? Number(e.target.value) : undefined })} aria-label={g.name} style={{ display: "block", width: "100%", minHeight: 44, fontSize: 16, borderRadius: 8, border: "1px solid #d1d5db", padding: "0 8px" }}>
                        {!g.required && <option value="">No thanks</option>}
                        {g.choices.map((c) => <option key={c.id} value={c.id} disabled={!c.is_available}>{c.label}{c.price_delta > 0 ? ` (+$${c.price_delta.toFixed(2)})` : ""}{!c.is_available ? " (sold out)" : ""}</option>)}
                      </select>
                    )}
                  </div>
                ))}
                <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
                  <button style={{ flex: 1, minHeight: 46, borderRadius: 8, border: "1px solid #d1d5db", background: "#f3f4f6", fontSize: 15, cursor: "pointer" }} onClick={() => setOptionItem(null)}>Cancel</button>
                  <button style={{ flex: 2, minHeight: 46, borderRadius: 8, border: "none", background: "#2563eb", color: "#fff", fontSize: 15, fontWeight: 600, cursor: "pointer" }} disabled={optionResolve(optionItem, optionPicks).missing} onClick={() => addWithOptions(optionItem, optionPicks)}>Add · ${(Number(optionItem.price) + optionResolve(optionItem, optionPicks).extra).toFixed(2)}</button>
                </div>
              </div>
            </div>
          )}

          {cartCount > 0 && (
            <div style={styles.readyPanel}>
              <div style={styles.readyHeader}>Ready To Send ({cartCount} new)</div>
              {cartEntries.map(([itemId, c]) => (
                <div key={itemId} style={styles.readyRow}>
                  <div style={{ minWidth: 0 }}>
                    <div style={styles.itemName}>{c.name}</div>
                    <div style={styles.readyPrice}>${Number(c.price).toFixed(2)} each{c.quantity > 1 ? ` · $${(Number(c.price) * c.quantity).toFixed(2)}` : ""}</div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: space[2] }}>
                    <button className="icon-btn qty-btn" style={styles.qtyBtnSmall} onClick={() => adjustCartQty(itemId, -1)} disabled={readOnly}>−</button>
                    <span style={{ color: colors.darkText, fontFamily: fonts.body, minWidth: 16, textAlign: "center" }}>{c.quantity}</span>
                    <button className="icon-btn qty-btn" style={styles.qtyBtnSmall} onClick={() => adjustCartQty(itemId, 1)} disabled={readOnly}>+</button>
                  </div>
                </div>
              ))}
              <div style={styles.readySubtotalRow}>
                <span>Subtotal (no tax or gratuity)</span>
                <span style={styles.readySubtotalAmount}>${Number(cartTotal).toFixed(2)}</span>
              </div>
              <button style={styles.sendBtn} onClick={sendToKitchen} disabled={readOnly}>
                Send {cartCount} Item{cartCount > 1 ? "s" : ""} to Kitchen
              </button>
            </div>
          )}

          {currentOrder && currentOrder.items.length > 0 && (
            <div style={styles.sentOrderSection}>
              <div style={styles.readyHeader}>Sent to Kitchen</div>
              {currentOrder.items.map((item) => (
                <div key={item.id} style={styles.readyRow}>
                  <span style={styles.itemName}>{item.quantity}× {item.item_name}</span>
                  <div style={{ display: "flex", alignItems: "center", gap: space[2] }}>
                    <span style={styles.orderItemStatus}>{item.status.replace("_", " ")}</span>
                    {item.status === "new" && (
                      <button style={styles.removeBtn} onClick={() => removeSentItem(item.id)} disabled={readOnly}>Remove</button>
                    )}
                    {item.status === "ready" && (
                      <button style={styles.receivedBtn} onClick={() => markItemReceived(item.id)} disabled={readOnly}>Received</button>
                    )}
                  </div>
                </div>
              ))}
              <div style={styles.subtotalRow}>
                <span>Subtotal</span>
                <span>${currentOrder.subtotal.toFixed(2)}</span>
              </div>
              <button style={{ ...buttonPrimary, width: "100%", marginTop: space[3] }} onClick={closeOrder} disabled={readOnly}>
                Close Order & Print Receipt
              </button>
            </div>
          )}

          <div style={styles.historySection}>
            <div style={styles.readyHeader}>Order History</div>
            {history.map((h, i) => (
              <div key={i} style={styles.historyRow}>
                <span>{h.quantity}× {h.item_name}</span>
                <span style={styles.servedPill}>{h.status}</span>
              </div>
            ))}
            {history.length === 0 && <p style={styles.emptyText}>No past orders for this table yet.</p>}
          </div>
        </div>
      )}

      <div style={styles.brandFooter}>
        <AlvnaBrand variant="compact" />
      </div>
    </div>
  );
}

function round2(n) {
  return Math.round(n * 100) / 100;
}

const styles = {
  page: { minHeight: "100vh", background: colors.darkBg, padding: space[5], display: "flex", flexDirection: "column", alignItems: "center" },
  header: { width: "100%", maxWidth: 900, display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: space[4] },
  restaurantTitle: { width: "100%", maxWidth: 900, textAlign: "center", fontFamily: fonts.display, fontWeight: 700, fontSize: 20, lineHeight: 1.25, color: colors.darkText, marginBottom: space[3], wordBreak: "break-word" },
  title: { fontFamily: fonts.display, color: colors.darkText, fontSize: 20, margin: 0, display: "flex", alignItems: "center" },
  backBtn: { background: "none", border: "none", color: colors.brandBlue, fontSize: 14, cursor: "pointer", fontFamily: fonts.body, fontWeight: 600 },
  ghostBtn: { background: colors.darkSurface, border: `1px solid ${colors.darkBorder}`, color: colors.darkText, borderRadius: radius.sm, padding: "12px 18px", fontSize: 14, cursor: "pointer", fontFamily: fonts.body, fontWeight: 600, lineHeight: 1.2, minHeight: 44, display: "flex", alignItems: "center" },
  splitPayBtn: { background: "#c0392b", border: "none", color: "#fff", borderRadius: radius.sm, padding: "7px 14px", fontSize: 13, cursor: "pointer", fontFamily: fonts.body, fontWeight: 600, lineHeight: 1.2 },
  logoutBtn: { background: "none", border: `1px solid ${colors.darkBorder}`, color: colors.darkText, borderRadius: radius.sm, padding: "12px 18px", fontSize: 14, cursor: "pointer", fontFamily: fonts.body, fontWeight: 600, lineHeight: 1.2, minHeight: 44, display: "flex", alignItems: "center" },
  managerHomeLink: { background: "none", border: `1px solid ${colors.darkBorder}`, color: colors.brandBlue, borderRadius: radius.sm, padding: "12px 18px", fontSize: 14, textDecoration: "none", fontFamily: fonts.body, fontWeight: 600, lineHeight: 1.2, display: "inline-flex", alignItems: "center", minHeight: 44 },
  staffName: { fontFamily: fonts.body, color: colors.textMuted, fontSize: 13 },
  errorBanner: { width: "100%", maxWidth: 900, background: "#7f1d1d", color: "#fecaca", padding: space[2], borderRadius: radius.sm, marginBottom: space[3], fontFamily: fonts.body, fontSize: 13 },
  overlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 },
  tableGrid: { width: "100%", maxWidth: 900, display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))", gap: space[3] },
  tableCard: { background: colors.darkSurface, border: `1px solid ${colors.darkBorder}`, borderRadius: radius.md, padding: space[4], cursor: "pointer", textAlign: "left" },
  tableOccupied: { borderColor: colors.statusProgress, background: "#3a2a12" },
  tableLabel: { fontFamily: fonts.display, color: colors.darkText, fontSize: 16, fontWeight: 600 },
  tableStatus: { fontFamily: fonts.body, color: colors.textMuted, fontSize: 12, marginTop: 4 },
  emptyText: { fontFamily: fonts.body, color: colors.textMuted, fontSize: 13 },
  noteBar: { display: "flex", justifyContent: "space-between", alignItems: "center", background: colors.darkSurface, border: `1px solid ${colors.darkBorder}`, borderRadius: radius.md, padding: space[3], gap: space[2] },
  noteText: { fontFamily: fonts.body, color: colors.textMuted, fontStyle: "italic", fontSize: 13 },
  noteInput: { flex: 1, minWidth: 0, padding: "6px 10px", borderRadius: radius.sm, border: `1px solid ${colors.darkBorder}`, background: colors.darkBg, color: colors.darkText, fontFamily: fonts.body, fontSize: 13 },
  editNoteBtn: { background: "none", border: `1px solid ${colors.darkBorder}`, color: colors.darkText, borderRadius: radius.sm, padding: "6px 12px", fontSize: 12, cursor: "pointer", whiteSpace: "nowrap" },
  detailsRow: { display: "flex", gap: space[3], alignItems: "flex-start", marginBottom: space[4], minWidth: 0 },
  smallLabel: { fontFamily: fonts.body, color: colors.textMuted, fontSize: 11, display: "block", marginBottom: 4 },
  smallInput: { padding: "8px 10px", borderRadius: radius.sm, border: `1px solid ${colors.darkBorder}`, background: colors.darkSurface, color: colors.darkText, fontFamily: fonts.body, fontSize: 13, width: 100 },
  saveBtn: { background: colors.darkSurface, border: `1px solid ${colors.darkBorder}`, color: colors.darkText, borderRadius: radius.sm, padding: "8px 14px", fontSize: 13, cursor: "pointer" },
  savedBadge: { fontFamily: fonts.body, fontSize: 13, color: colors.statusReady, fontWeight: 600, alignSelf: "center", marginLeft: space[1] },
  categoryPillsRow: { display: "flex", gap: space[2], overflowX: "auto", marginBottom: space[3], position: "sticky", top: 0, background: colors.darkBg, paddingBottom: space[2], zIndex: 10 },
  categoryPill: { padding: "8px 16px", borderRadius: radius.pill, border: `1px solid ${colors.darkBorder}`, background: colors.darkSurface, color: colors.darkText, fontSize: 13, cursor: "pointer", whiteSpace: "nowrap" },
  categorySection: { marginBottom: space[4] },
  categoryHeading: { fontFamily: fonts.display, color: colors.darkText, fontSize: 16, marginBottom: space[2] },
  itemGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: space[3] },
  itemCard: { position: "relative", background: colors.darkSurface, border: `1px solid ${colors.darkBorder}`, borderRadius: radius.md, padding: space[3], cursor: "pointer", textAlign: "left" },
  itemCardSelected: { borderColor: colors.brandBlue, boxShadow: `0 0 0 1px ${colors.brandBlue}` },
  itemName: { fontFamily: fonts.body, color: colors.darkText, fontSize: 14, fontWeight: 500 },
  readyPrice: { fontFamily: fonts.body, color: colors.textMuted, fontSize: 12, marginTop: 2 },
  itemPrice: { fontFamily: fonts.body, color: colors.textMuted, fontSize: 13, marginTop: 4 },
  itemBadge: { position: "absolute", bottom: -6, right: -6, background: colors.brandGradient, color: "#fff", borderRadius: radius.pill, width: 22, height: 22, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700 },
  readyPanel: { background: colors.darkSurface, border: `1px solid ${colors.darkBorder}`, borderRadius: radius.md, padding: space[4], marginTop: space[4] },
  sentOrderSection: { background: colors.darkSurface, border: `1px solid ${colors.darkBorder}`, borderRadius: radius.md, padding: space[4], marginTop: space[4] },
  readyHeader: { fontFamily: fonts.display, color: colors.darkText, fontSize: 15, fontWeight: 600, marginBottom: space[3] },
  readyRow: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: `${space[2]} 0`, borderBottom: `1px solid ${colors.darkBorder}` },
  qtyBtnSmall: { width: 36, height: 36, borderRadius: radius.pill, border: `1px solid ${colors.darkBorder}`, background: colors.darkBg, color: colors.darkText, fontSize: 16, cursor: "pointer", touchAction: "manipulation" },
  readySubtotalRow: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: `${space[3]} 0 0`, fontFamily: fonts.body, color: colors.textMuted, fontSize: 13 },
  readySubtotalAmount: { fontFamily: fonts.display, color: colors.darkText, fontSize: 17, fontWeight: 700 },
  sendBtn: { ...buttonPrimary, width: "100%", marginTop: space[3] },
  orderItemStatus: { fontFamily: fonts.body, color: colors.statusProgress, fontSize: 11, textTransform: "uppercase" },
  removeBtn: { background: "none", border: "none", color: "#fca5a5", fontSize: 13, cursor: "pointer", padding: "8px 6px" },
  receivedBtn: { background: colors.brandGradient, border: "none", color: "#fff", fontSize: 12, fontWeight: 600, padding: "8px 14px", borderRadius: radius.pill, cursor: "pointer" },
  subtotalRow: { display: "flex", justifyContent: "space-between", marginTop: space[3], fontFamily: fonts.display, color: colors.darkText, fontSize: 16, fontWeight: 600 },
  historySection: { marginTop: space[5] },
  historyRow: { display: "flex", justifyContent: "space-between", padding: `${space[2]} 0`, borderBottom: `1px solid ${colors.darkBorder}`, fontFamily: fonts.body, color: colors.darkText, fontSize: 14 },
  servedPill: { background: colors.darkSurface, border: `1px solid ${colors.darkBorder}`, color: colors.textMuted, fontSize: 11, padding: "2px 10px", borderRadius: radius.pill },
  brandFooter: { marginTop: space[5] },
};

export default function WaiterPage() {
  return <TabbedStation Board={WaiterBoard} title="Tables & orders" dark={false} />;
}
